import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { Capacitor } from '@capacitor/core';
import AddChildModal from '../components/AddChildModal';

// Helper: Register current device for a new child profile
const registerDeviceForChild = async (childId) => {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const OneSignal = window.plugins?.OneSignal || window.OneSignal;
    if (!OneSignal) return;

    const playerId = OneSignal.User?.pushSubscription?.id;
    if (!playerId) {
      console.log('[ProfilePicker] No player ID available for child registration');
      return;
    }

    const platform = Capacitor.getPlatform();
    const { error } = await supabase
      .from('user_devices')
      .upsert({
        user_id: childId,
        onesignal_player_id: playerId,
        platform: platform,
        device_name: `${platform} device`,
        last_active_at: new Date().toISOString()
      }, { onConflict: 'user_id,onesignal_player_id' });

    if (error) {
      console.error('[ProfilePicker] Error registering device for child:', error);
    } else {
      console.log('[ProfilePicker] Device registered for child:', childId);
    }
  } catch (err) {
    console.error('[ProfilePicker] Device registration failed:', err);
  }
};

// Icons
const UserIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const PlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const LogOutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16,17 21,12 16,7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ProfilePicker = () => {
  const { user, signOut } = useAuth();
  const { switchProfile, activeProfileId, refreshProfile } = useProfile();
  const { theme, isDark } = useTheme();
  const navigate = useNavigate();
  const [parentProfile, setParentProfile] = useState(null);
  const [linkedKids, setLinkedKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setAsDefault, setSetAsDefault] = useState(false);

  // Trade toggle password prompt (replaces old edit-child flow)
  const [showTradePasswordPrompt, setShowTradePasswordPrompt] = useState(false);
  const [tradeToggleLink, setTradeToggleLink] = useState(null);
  const [tradePassword, setTradePassword] = useState('');
  const [tradePasswordError, setTradePasswordError] = useState('');
  const [tradeToggleSaving, setTradeToggleSaving] = useState(false);

  // Password verification for switching profiles
  const [showSwitchPasswordPrompt, setShowSwitchPasswordPrompt] = useState(false);
  const [pendingSwitchProfileId, setPendingSwitchProfileId] = useState(null);
  const [switchPassword, setSwitchPassword] = useState('');
  const [switchPasswordError, setSwitchPasswordError] = useState('');
  const [switchingProfile, setSwitchingProfile] = useState(false);

  // Incoming child requests
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [processingRequest, setProcessingRequest] = useState(null);

  // Add child modal
  const [showAddChildModal, setShowAddChildModal] = useState(false);

  useEffect(() => {
    const fetchProfiles = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch parent profile
        const { data: parentData, error: parentError } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', user.id)
          .single();

        if (parentError && parentError.code !== 'PGRST116') {
          console.error('Error fetching parent profile:', parentError);
        }

        if (parentData) {
          setParentProfile(parentData);

          // Fetch linked kids
          const { data: links, error: linksError } = await supabase
            .from('family_links')
            .select(`
              id,
              can_trade,
              child_id,
              users!family_links_child_id_fkey (
                id,
                first_name,
                last_name,
                username
              )
            `)
            .eq('parent_id', parentData.id);

          if (linksError) {
            console.error('Error fetching linked kids:', linksError);
          } else {
            setLinkedKids(links || []);
          }

          // Fetch incoming child requests (pending)
          console.log('[ProfilePicker] Fetching incoming requests for parent:', parentData.id);
          const { data: requests, error: requestsError } = await supabase
            .from('parent_child_links')
            .select(`
              id,
              child_id,
              status,
              can_trade,
              created_at,
              child:users!parent_child_links_child_id_fkey (
                id,
                first_name,
                last_name,
                username,
                avatar_url
              )
            `)
            .eq('parent_id', parentData.id)
            .eq('status', 'pending');

          console.log('[ProfilePicker] Incoming requests result:', { requests, requestsError });

          if (requestsError) {
            console.error('Error fetching requests:', requestsError);
          } else {
            // Filter out requests for children that are already linked via family_links
            // This handles cases where the request wasn't properly marked as 'active'
            const linkedChildIds = (links || []).map(l => l.child_id);
            const filteredRequests = (requests || []).filter(r => !linkedChildIds.includes(r.child_id));

            // If we filtered out any requests, clean them up in the database
            const staleRequests = (requests || []).filter(r => linkedChildIds.includes(r.child_id));
            if (staleRequests.length > 0) {
              console.log('[ProfilePicker] Cleaning up stale requests:', staleRequests.map(r => r.id));
              // Update stale requests to 'active' status since the child is already linked
              for (const stale of staleRequests) {
                supabase
                  .from('parent_child_links')
                  .update({ status: 'active' })
                  .eq('id', stale.id)
                  .then(() => console.log('[ProfilePicker] Cleaned up stale request:', stale.id));
              }
            }

            setIncomingRequests(filteredRequests);
          }
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [user?.id]);

  const handleSelectProfile = async (profileId) => {
    // Determine if this is the currently active profile
    // activeProfileId is null when viewing as parent (self), or a child_id when viewing as child
    const currentProfile = activeProfileId || parentProfile?.id;
    const isSameProfile = profileId === currentProfile;

    // If selecting the same profile, just navigate (no password needed)
    if (isSameProfile) {
      navigate('/home');
      return;
    }

    // Switching to a different profile - require password
    setPendingSwitchProfileId(profileId);
    setSwitchPassword('');
    setSwitchPasswordError('');
    setShowSwitchPasswordPrompt(true);
  };

  // Complete profile switch after password verification
  const completeSwitchProfile = async (profileId) => {
    // Set active profile in context
    switchProfile(profileId);

    // Always save the selection to database (required for routing to work)
    if (parentProfile) {
      try {
        await supabase
          .from('users')
          .update({ default_profile_id: profileId })
          .eq('id', parentProfile.id);
      } catch (err) {
        console.error('Error saving default profile:', err);
      }
    }

    // Navigate to home
    navigate('/home');
  };

  // Verify password and switch profile
  const verifyPasswordAndSwitch = async () => {
    if (!switchPassword.trim()) {
      setSwitchPasswordError('Please enter your password');
      return;
    }

    setSwitchingProfile(true);

    try {
      // Re-authenticate with Supabase using current user's email
      const { error } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: switchPassword,
      });

      if (error) {
        setSwitchPasswordError('Incorrect password');
        setSwitchingProfile(false);
        return;
      }

      // Password correct - complete the switch
      setShowSwitchPasswordPrompt(false);
      setSwitchPassword('');
      setSwitchPasswordError('');
      await completeSwitchProfile(pendingSwitchProfileId);
    } catch (err) {
      setSwitchPasswordError('Verification failed');
      setSwitchingProfile(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const openTradeTogglePrompt = (link) => {
    setTradeToggleLink(link);
    setTradePassword('');
    setTradePasswordError('');
    setShowTradePasswordPrompt(true);
  };

  const verifyPasswordAndToggleTrade = async () => {
    if (!tradeToggleLink) return;
    if (!tradePassword.trim()) {
      setTradePasswordError('Please enter your password');
      return;
    }
    setTradeToggleSaving(true);
    try {
      const { error: authErr } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: tradePassword,
      });
      if (authErr) {
        setTradePasswordError('Incorrect password');
        return;
      }
      const newValue = !tradeToggleLink.can_trade;
      const { error: dbErr } = await supabase
        .from('family_links')
        .update({ can_trade: newValue })
        .eq('id', tradeToggleLink.id);
      if (dbErr) {
        setTradePasswordError('Could not update. Please try again.');
        return;
      }
      setLinkedKids(prev => prev.map(l => l.id === tradeToggleLink.id ? { ...l, can_trade: newValue } : l));
      await refreshProfile();
      setShowTradePasswordPrompt(false);
      setTradeToggleLink(null);
      setTradePassword('');
      setTradePasswordError('');
    } catch (err) {
      setTradePasswordError('Verification failed');
    } finally {
      setTradeToggleSaving(false);
    }
  };

  // Handle accepting/declining a child request
  const handleChildRequest = async (request, accept) => {
    if (processingRequest) return;
    setProcessingRequest(request.id);

    try {
      if (accept) {
        // Accept: update link status AND create family_link
        const { error: linkError } = await supabase
          .from('parent_child_links')
          .update({ status: 'active', can_trade: false })
          .eq('id', request.id);

        if (linkError) throw linkError;

        // Also create a family_link entry
        const { error: familyError } = await supabase
          .from('family_links')
          .insert({
            parent_id: parentProfile.id,
            child_id: request.child_id,
            can_trade: false,
          });

        if (familyError && familyError.code !== '23505') { // Ignore duplicate key
          console.error('Family link error:', familyError);
        }

        // Make sure this user is marked as a parent
        if (!parentProfile?.is_parent) {
          await supabase
            .from('users')
            .update({ is_parent: true })
            .eq('id', parentProfile.id);

          setParentProfile(prev => ({ ...prev, is_parent: true }));
        }

        // Refresh linked kids
        const { data: links } = await supabase
          .from('family_links')
          .select(`
            id,
            can_trade,
            child_id,
            users!family_links_child_id_fkey (
              id,
              first_name,
              last_name,
              username
            )
          `)
          .eq('parent_id', parentProfile.id);

        setLinkedKids(links || []);

        // Register device for the newly accepted child
        await registerDeviceForChild(request.child_id);

        // Refresh ProfileContext so permissions are recalculated
        await refreshProfile();
      } else {
        // Decline: delete the link
        const { error } = await supabase
          .from('parent_child_links')
          .delete()
          .eq('id', request.id);

        if (error) throw error;
      }

      // Re-fetch pending requests from DB to verify removal (instead of optimistic update)
      const { data: updatedRequests, error: refetchError } = await supabase
        .from('parent_child_links')
        .select(`
          id,
          child_id,
          status,
          can_trade,
          created_at,
          child:users!parent_child_links_child_id_fkey (
            id,
            first_name,
            last_name,
            username,
            avatar_url
          )
        `)
        .eq('parent_id', parentProfile.id)
        .eq('status', 'pending');

      if (refetchError) {
        console.error('Error re-fetching requests:', refetchError);
        // Fallback to optimistic removal if re-fetch fails
        setIncomingRequests(prev => prev.filter(r => r.id !== request.id));
      } else {
        setIncomingRequests(updatedRequests || []);
      }
    } catch (err) {
      console.error('Error handling request:', err);
      alert('Failed to process request. Please try again.');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle child added from modal
  const handleChildAdded = async () => {
    // Refresh linked kids
    const { data: links } = await supabase
      .from('family_links')
      .select(`
        id,
        can_trade,
        child_id,
        users!family_links_child_id_fkey (
          id,
          first_name,
          last_name,
          username
        )
      `)
      .eq('parent_id', parentProfile.id);

    setLinkedKids(links || []);

    // Register device for all children (ensures new child gets notifications)
    if (links) {
      for (const link of links) {
        await registerDeviceForChild(link.child_id);
      }
    }

    // Make sure user is marked as parent
    if (!parentProfile?.is_parent) {
      await supabase
        .from('users')
        .update({ is_parent: true })
        .eq('id', parentProfile.id);

      setParentProfile(prev => ({ ...prev, is_parent: true }));
    }

    // Refresh ProfileContext so new child is available for profile switching
    await refreshProfile();
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    spinner: {
      width: '32px',
      height: '32px',
      border: `3px solid ${theme.border}`,
      borderTopColor: theme.accent,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '48px 24px',
      maxWidth: '400px',
      margin: '0 auto',
      width: '100%',
    },
    topBar: {
      backgroundColor: theme.bgCard,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      borderBottom: `1px solid ${theme.border}`,
    },
    backButton: {
      background: 'none',
      border: 'none',
      padding: 8,
      cursor: 'pointer',
      color: theme.accent,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    topBarTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px',
    },
    requestsSection: {
      width: '100%',
      marginBottom: '24px',
      backgroundColor: isDark ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
      borderRadius: '12px',
      padding: '16px',
      border: isDark ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(139, 92, 246, 0.2)',
    },
    requestsTitle: {
      color: '#8b5cf6',
      fontSize: '14px',
      fontWeight: '600',
      margin: '0 0 12px 0',
    },
    requestCard: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.bgCard,
      borderRadius: '10px',
      padding: '12px',
      marginBottom: '8px',
    },
    requestInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    },
    requestName: {
      color: theme.text,
      fontSize: '15px',
      fontWeight: '600',
    },
    requestUsername: {
      color: theme.textSecondary,
      fontSize: '13px',
    },
    requestActions: {
      display: 'flex',
      gap: '8px',
    },
    declineButton: {
      padding: '8px 12px',
      backgroundColor: 'transparent',
      border: isDark ? '1px solid #4a4a4a' : `1px solid ${theme.border}`,
      borderRadius: '8px',
      color: isDark ? '#8a8a8a' : theme.textMuted,
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
    },
    acceptButton: {
      padding: '8px 12px',
      backgroundColor: theme.success,
      border: 'none',
      borderRadius: '8px',
      color: theme.text,
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    title: {
      color: theme.text,
      fontSize: '24px',
      fontWeight: '700',
      margin: '0 0 8px 0',
    },
    subtitle: {
      color: theme.textSecondary,
      fontSize: '14px',
      margin: 0,
    },
    currentProfileName: {
      color: theme.accent,
      fontWeight: '600',
    },
    profileGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
      width: '100%',
      marginBottom: '32px',
    },
    profileCard: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      padding: '24px 16px',
      backgroundColor: theme.bgSheet,
      border: `2px solid ${theme.border}`,
      borderRadius: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    activeProfileCard: {
      border: `2px solid ${theme.accent}`,
      boxShadow: isDark ? '0 0 20px rgba(14, 165, 233, 0.3)' : '0 0 20px rgba(14, 165, 233, 0.15)',
      backgroundColor: isDark ? 'rgba(14, 165, 233, 0.08)' : 'rgba(14, 165, 233, 0.04)',
    },
    profileAvatar: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: isDark ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.accent,
      marginBottom: '4px',
    },
    profileName: {
      color: theme.text,
      fontSize: '16px',
      fontWeight: '600',
    },
    profileRole: {
      color: theme.textSecondary,
      fontSize: '12px',
    },
    profilePermission: {
      fontSize: '12px',
      fontWeight: '500',
    },
    currentLabel: {
      fontSize: '12px',
      fontWeight: '500',
      color: theme.accent,
    },
    activeLabel: {
      fontSize: '11px',
      fontWeight: '700',
      color: theme.text,
      backgroundColor: theme.accent,
      padding: '4px 10px',
      borderRadius: '12px',
      marginTop: '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    addProfileCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      padding: '24px 16px',
      backgroundColor: 'transparent',
      border: `2px dashed ${theme.border}`,
      borderRadius: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    addProfileIcon: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: isDark ? 'rgba(106, 106, 106, 0.1)' : 'rgba(106, 106, 106, 0.05)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.textSecondary,
      marginBottom: '4px',
    },
    addProfileText: {
      color: theme.textSecondary,
      fontSize: '14px',
      fontWeight: '500',
    },
    rememberToggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      marginBottom: '24px',
    },
    checkbox: {
      width: '24px',
      height: '24px',
      borderRadius: '6px',
      border: '2px solid',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.text,
      transition: 'all 0.2s ease',
    },
    rememberText: {
      color: isDark ? '#8a8a8a' : theme.textMuted,
      fontSize: '14px',
    },
    signOutButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '12px 24px',
      backgroundColor: 'transparent',
      border: isDark ? '1px solid #4a4a4a' : `1px solid ${theme.border}`,
      borderRadius: '10px',
      color: theme.textSecondary,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      marginTop: 'auto',
    },
    switchToProfileButton: {
      width: '100%',
      marginTop: '10px',
      padding: '10px 12px',
      backgroundColor: 'transparent',
      border: `1px solid ${theme.accent}`,
      borderRadius: '10px',
      color: theme.accent,
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    tradeSettingsRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      marginTop: '10px',
      padding: '10px 12px',
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
      borderRadius: '10px',
      gap: '12px',
    },
    tradeSettingsLabel: {
      color: theme.text,
      fontSize: '13px',
      fontWeight: '600',
    },
    toggleTrack: {
      position: 'relative',
      width: '44px',
      height: '24px',
      borderRadius: '999px',
      border: 'none',
      padding: '2px',
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
    },
    toggleThumb: {
      display: 'block',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'transform 0.15s',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      zIndex: 1000,
    },
    modal: {
      backgroundColor: theme.bgSheet,
      borderRadius: '16px',
      width: '100%',
      maxWidth: '340px',
      border: `1px solid ${theme.border}`,
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px',
      borderBottom: `1px solid ${theme.border}`,
    },
    modalTitle: {
      color: theme.text,
      fontSize: '18px',
      fontWeight: '600',
      margin: 0,
    },
    closeButton: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      backgroundColor: 'transparent',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.textSecondary,
      cursor: 'pointer',
    },
    modalContent: {
      padding: '16px',
    },
    inputGroup: {
      marginBottom: '16px',
    },
    inputLabel: {
      display: 'block',
      color: isDark ? '#8a8a8a' : theme.textMuted,
      fontSize: '13px',
      fontWeight: '500',
      marginBottom: '8px',
    },
    input: {
      width: '100%',
      padding: '12px',
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderRadius: '10px',
      color: theme.text,
      fontSize: '15px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    saveButton: {
      width: '100%',
      padding: '14px',
      backgroundColor: theme.accent,
      border: 'none',
      borderRadius: '0 0 16px 16px',
      color: '#fff',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    errorText: {
      color: theme.error,
      fontSize: '13px',
      margin: '8px 0 0 0',
    },
    switchPasswordDesc: {
      color: isDark ? '#8a8a8a' : theme.textMuted,
      fontSize: '14px',
      margin: '0 0 16px 0',
      lineHeight: 1.4,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  // Determine current profile name for header display
  const getCurrentProfileName = () => {
    if (!activeProfileId || activeProfileId === parentProfile?.id) {
      return parentProfile?.first_name || 'Parent';
    }
    const activeKid = linkedKids.find(k => k.child_id === activeProfileId);
    return activeKid?.users?.first_name || 'Child';
  };

  const isParentActive = !activeProfileId || activeProfileId === parentProfile?.id;

  return (
    <div style={styles.container}>
      {/* Top bar - matches canonical pattern (Preferences, NotificationSettings, Profile) */}
      <div style={styles.topBar}>
        <button style={styles.backButton} onClick={() => navigate('/profile')} aria-label="Back to Profile">
          <BackIcon />
        </button>
        <h1 style={styles.topBarTitle}>Profiles</h1>
      </div>

      <div style={styles.content}>
        {/* Welcome heading */}
        <div style={styles.header}>
          <h1 style={styles.title}>Who's using CardChase?</h1>
          <p style={styles.subtitle}>
            Currently signed in as <span style={styles.currentProfileName}>{getCurrentProfileName()}</span>
          </p>
        </div>

        {/* Incoming Child Requests */}
        {incomingRequests.length > 0 && (
          <div style={styles.requestsSection}>
            <h3 style={styles.requestsTitle}>Child Requests</h3>
            {incomingRequests.map((request) => (
              <div key={request.id} style={styles.requestCard}>
                <div style={styles.requestInfo}>
                  <span style={styles.requestName}>
                    {request.child?.first_name || request.child?.username || 'Unknown'}
                  </span>
                  {request.child?.username && request.child?.first_name && (
                    <span style={styles.requestUsername}>@{request.child.username}</span>
                  )}
                </div>
                <div style={styles.requestActions}>
                  <button
                    style={styles.declineButton}
                    onClick={() => handleChildRequest(request, false)}
                    disabled={processingRequest === request.id}
                  >
                    Decline
                  </button>
                  <button
                    style={styles.acceptButton}
                    onClick={() => handleChildRequest(request, true)}
                    disabled={processingRequest === request.id}
                  >
                    {processingRequest === request.id ? '...' : 'Accept'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Profile Grid */}
        <div style={styles.profileGrid}>
          {/* Parent Profile */}
          {parentProfile && (
            <div style={{ ...styles.profileCard, ...(isParentActive ? styles.activeProfileCard : {}) }}>
              <div style={styles.profileAvatar}><UserIcon /></div>
              <span style={styles.profileName}>{parentProfile.first_name || 'Me'}</span>
              <span style={styles.profileRole}>Parent</span>
              {isParentActive ? (
                <span style={styles.activeLabel}>Signed In</span>
              ) : (
                <button style={styles.switchToProfileButton} onClick={() => handleSelectProfile(parentProfile.id)}>
                  Switch to this profile
                </button>
              )}
            </div>
          )}

          {/* Kid Profiles */}
          {linkedKids.map((link) => {
            const isActive = activeProfileId === link.child_id;
            return (
              <div
                key={link.id}
                style={{ ...styles.profileCard, ...(isActive ? styles.activeProfileCard : {}) }}
              >
                <div style={{
                  ...styles.profileAvatar,
                  backgroundColor: 'rgba(139, 92, 246, 0.2)',
                  color: '#8b5cf6',
                }}>
                  <UserIcon />
                </div>
                <span style={styles.profileName}>{link.users?.first_name || 'Child'}</span>
                <span style={styles.profileRole}>Child</span>

                {isActive ? (
                  <span style={styles.activeLabel}>Signed In</span>
                ) : (
                  <button style={styles.switchToProfileButton} onClick={() => handleSelectProfile(link.child_id)}>
                    Switch to this profile
                  </button>
                )}

                {/* Trade Settings */}
                <div style={styles.tradeSettingsRow}>
                  <span style={styles.tradeSettingsLabel}>
                    Trading {link.can_trade ? 'On' : 'Off'}
                  </span>
                  <button
                    type="button"
                    onClick={() => openTradeTogglePrompt(link)}
                    aria-pressed={!!link.can_trade}
                    aria-label={`Toggle trading for ${link.users?.first_name || 'this child'}`}
                    style={{
                      ...styles.toggleTrack,
                      backgroundColor: link.can_trade ? '#22c55e' : (isDark ? '#3a3a3a' : '#d4d4d8'),
                    }}
                  >
                    <span style={{ ...styles.toggleThumb, transform: link.can_trade ? 'translateX(20px)' : 'translateX(0)' }} />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add Child Button */}
          <button
            style={styles.addProfileCard}
            onClick={() => setShowAddChildModal(true)}
          >
            <div style={styles.addProfileIcon}>
              <PlusIcon />
            </div>
            <span style={styles.addProfileText}>Add Child</span>
          </button>
        </div>

        {/* Remember Choice Toggle */}
        <button
          style={styles.rememberToggle}
          onClick={() => setSetAsDefault(!setAsDefault)}
        >
          <div style={{
            ...styles.checkbox,
            backgroundColor: setAsDefault ? theme.accent : 'transparent',
            borderColor: setAsDefault ? theme.accent : (isDark ? '#4a4a4a' : theme.border),
          }}>
            {setAsDefault && <CheckIcon />}
          </div>
          <span style={styles.rememberText}>Remember my choice</span>
        </button>

        {/* Sign Out */}
        <button style={styles.signOutButton} onClick={handleSignOut}>
          <LogOutIcon />
          <span>Sign Out</span>
        </button>
      </div>

      {/* Trade Toggle Password Modal */}
      {showTradePasswordPrompt && tradeToggleLink && (
        <div style={styles.modalOverlay} onClick={() => {
          if (tradeToggleSaving) return;
          setShowTradePasswordPrompt(false);
          setTradeToggleLink(null);
          setTradePassword('');
          setTradePasswordError('');
        }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Change Trade Setting</h2>
              <button style={styles.closeButton} onClick={() => {
                if (tradeToggleSaving) return;
                setShowTradePasswordPrompt(false);
                setTradeToggleLink(null);
                setTradePassword('');
                setTradePasswordError('');
              }}>
                <XIcon />
              </button>
            </div>
            <div style={styles.modalContent}>
              <p style={styles.switchPasswordDesc}>
                In order to change this setting you need the account owner's password.
                Trading for <strong>{tradeToggleLink.users?.first_name || 'this child'}</strong> will be turned <strong>{tradeToggleLink.can_trade ? 'OFF' : 'ON'}</strong>.
              </p>
              <input
                type="password"
                value={tradePassword}
                onChange={(e) => setTradePassword(e.target.value)}
                style={styles.input}
                placeholder="Account owner's password"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && verifyPasswordAndToggleTrade()}
              />
              {tradePasswordError && (
                <p style={styles.errorText}>{tradePasswordError}</p>
              )}
            </div>
            <button
              style={{ ...styles.saveButton, opacity: tradeToggleSaving || !tradePassword.trim() ? 0.5 : 1 }}
              onClick={verifyPasswordAndToggleTrade}
              disabled={tradeToggleSaving || !tradePassword.trim()}
            >
              {tradeToggleSaving ? 'Saving...' : 'Confirm'}
            </button>
          </div>
        </div>
      )}

      {/* Switch Profile Password Modal */}
      {showSwitchPasswordPrompt && (
        <div style={styles.modalOverlay} onClick={() => {
          setShowSwitchPasswordPrompt(false);
          setSwitchPassword('');
          setSwitchPasswordError('');
          setSwitchingProfile(false);
        }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Enter Password</h2>
              <button style={styles.closeButton} onClick={() => {
                setShowSwitchPasswordPrompt(false);
                setSwitchPassword('');
                setSwitchPasswordError('');
                setSwitchingProfile(false);
              }}>
                <XIcon />
              </button>
            </div>

            <div style={styles.modalContent}>
              <p style={styles.switchPasswordDesc}>
                Enter your password to switch profiles.
              </p>
              <input
                type="password"
                value={switchPassword}
                onChange={(e) => setSwitchPassword(e.target.value)}
                style={styles.input}
                placeholder="Password"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && verifyPasswordAndSwitch()}
              />
              {switchPasswordError && (
                <p style={styles.errorText}>{switchPasswordError}</p>
              )}
            </div>

            <button
              style={{
                ...styles.saveButton,
                opacity: switchingProfile || !switchPassword.trim() ? 0.5 : 1,
              }}
              onClick={verifyPasswordAndSwitch}
              disabled={switchingProfile || !switchPassword.trim()}
            >
              {switchingProfile ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {/* Add Child Modal */}
      <AddChildModal
        isOpen={showAddChildModal}
        onClose={() => setShowAddChildModal(false)}
        parentId={parentProfile?.id}
        onChildAdded={handleChildAdded}
      />
    </div>
  );
};

export default ProfilePicker;
